import {
  GoogleGenAI,
  Modality,
  type FunctionCall,
  type FunctionResponse,
  type LiveServerMessage,
  type Session,
} from '@google/genai'
import type { ScanConfig, SessionCallbacks, AbortReason } from './types.js'
import { mintLiveToken, verifyCapture } from '../lib/api.js'
import {
  base64ToBytes,
  bytesToBase64,
  downsampleFloat32ToPcm16,
  pcmToWavBlob,
} from './audio.js'

/**
 * Cap saved-picture data URLs at this longest edge before storing. 1600 is
 * sharp enough to read label text when the user pinch-zooms in the preview
 * modal, while keeping six pictures comfortably under the 5 MB
 * sessionStorage cap (~300 KB base64 each at q=0.92).
 */
const SAVED_PICTURE_MAX_EDGE = 1600
const SAVED_PICTURE_JPEG_QUALITY = 0.92

/** Gemini Live returns 24 kHz mono PCM for AUDIO modality responses. */
const GEMINI_OUTPUT_SAMPLE_RATE = 24_000
/** Gemini Live expects 16 kHz mono Int16 PCM from the client microphone. */
const GEMINI_INPUT_SAMPLE_RATE = 16_000
/** Video frame pump cadence — the Live API processes video at ~1 FPS. */
const FRAME_INTERVAL_MS = 1_000
/** Outbound video frame size; small enough for low latency, big enough for OCR. */
const FRAME_WIDTH = 640
const FRAME_HEIGHT = 480
const FRAME_QUALITY = 0.7
/**
 * Mic-frame peak below this threshold counts as silence while we're waiting
 * for the user to start speaking after the model has finished. The mic loop
 * suppresses sub-threshold frames so the model doesn't re-hear its own audio
 * echo as user input.
 */
const SILENCE_PEAK_THRESHOLD = 0.01

/** Convert a video frame on a hidden canvas into a base64 JPEG. */
export const sampleFrame = (
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
  quality: number,
): string => {
  // Defensive: if the host canvas hasn't been painted yet, toDataURL will
  // return a valid-but-empty JPEG that Gemini logs as a black frame. Skip.
  if (canvas.width === 0 || canvas.height === 0) return ''
  const tmp = document.createElement('canvas')
  tmp.width = width; tmp.height = height
  const ctx = tmp.getContext('2d')
  if (!ctx) throw new Error('canvas 2d unsupported')
  ctx.drawImage(canvas, 0, 0, width, height)
  return tmp.toDataURL('image/jpeg', quality).split(',')[1] ?? ''
}

/**
 * Asynchronously downscale a data: URL to a max edge. Used by save_picture
 * for the stored still. JPEG quality is taken from SAVED_PICTURE_JPEG_QUALITY
 * — q ≥ 0.9 keeps text on label stickers readable when the user zooms in.
 */
export const downscaleDataUrl = async (dataUrlBase64: string, maxEdge: number): Promise<string> => {
  const img = new Image()
  img.src = dataUrlBase64.startsWith('data:') ? dataUrlBase64 : `data:image/jpeg;base64,${dataUrlBase64}`
  await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = () => rej(new Error('image load failed')) })
  const ratio = Math.min(1, maxEdge / Math.max(img.width, img.height))
  const w = Math.round(img.width * ratio), h = Math.round(img.height * ratio)
  const c = document.createElement('canvas')
  c.width = w; c.height = h
  const ctx = c.getContext('2d')
  if (!ctx) throw new Error('canvas 2d unsupported')
  ctx.drawImage(img, 0, 0, w, h)
  return c.toDataURL('image/jpeg', SAVED_PICTURE_JPEG_QUALITY)
}

export const parseToolCall = (msg: unknown): { name: string; args: unknown } | null => {
  const m = msg as { functionCalls?: Array<{ name: string; args: unknown }> }
  if (Array.isArray(m.functionCalls) && m.functionCalls.length > 0) {
    const first = m.functionCalls[0]
    if (first) return { name: first.name, args: first.args }
  }
  return null
}

interface OpenArgs {
  config: ScanConfig
  /**
   * Combined source MediaStream returned by getUserMedia. LiveSession only
   * borrows this — the parent screen owns its lifetime (via
   * `useGrantLifetime`) and stops the tracks at unmount. Killing the stream
   * from LiveSession.close() would take the visible <video> down with it on
   * any transient failure.
   */
  mediaStream: MediaStream
  audioStream: MediaStream
  hiddenCanvas: HTMLCanvasElement    // canvas painted with the current frame at full resolution
  /**
   * AudioContext created synchronously inside the camera-permission user gesture
   * (`useRequestCameraGrant`). iOS Safari only honors `AudioContext` if it's
   * constructed inside the same gesture that grants getUserMedia — creating it
   * later (e.g. on setupComplete) leaves it suspended forever and
   * ScriptProcessorNode.onaudioprocess never fires. LiveSession consumes this
   * context but does NOT close it on shutdown; the parent owns its lifetime.
   */
  audioContext: AudioContext
  callbacks: SessionCallbacks
}

/**
 * LiveSession owns the Gemini Live API WebSocket for one scan.
 *
 * Built on `@google/genai` 2.4's `client.live.connect(...)`. The token is an
 * `auth_tokens/...` ephemeral token minted by `POST /api/live-token`; the
 * server locks the model to GEMINI_LIVE_MODEL via `liveConnectConstraints`,
 * and returns the model name in the mint response so the SPA passes the same
 * value to `client.live.connect({ model })` without hardcoding it.
 */
export class LiveSession {
  private session: Session | null = null
  private setupComplete = false
  private frameTimer: number | null = null
  private timeoutHandle: number | null
  private audioSource: MediaStreamAudioSourceNode | null = null
  private audioProcessor: ScriptProcessorNode | null = null
  private aborted = false
  /** PCM chunks for the current Gemini speaking turn (assembled into one WAV per turn). */
  private inboundAudioChunks: Uint8Array[] = []
  /**
   * After we send `audioStreamEnd` on a turn boundary, suppress mic frames whose
   * peak amplitude is below SILENCE_PEAK_THRESHOLD so the model doesn't re-hear
   * its own playback through the still-open mic. Cleared when the user speaks.
   */
  private pendingAudioEnd = false

  private constructor(
    private readonly args: OpenArgs,
    private readonly token: string,
    private readonly model: string,
    timeoutHandle: number | null,
  ) {
    this.timeoutHandle = timeoutHandle
  }

  static async open(args: OpenArgs): Promise<LiveSession> {
    // Arm the safety-net timeout BEFORE the mint so a stalled token request
    // can still trip a UI abort. The handle is then handed to the session
    // (which clears it on close) or fired here (which aborts the mint).
    let timedOut = false
    const timeoutHandle = window.setTimeout(() => {
      timedOut = true
      args.callbacks.onAbort('timeout')
    }, args.config.timeoutMs)

    let token: string
    let model: string
    try {
      const t = await mintLiveToken(args.config.id)
      if (timedOut) throw new Error('timeout fired during token mint')
      token = t.token
      model = t.model
    } catch (e) {
      window.clearTimeout(timeoutHandle)
      if (!timedOut) args.callbacks.onAbort('token-failed', String(e))
      throw e
    }

    const s = new LiveSession(args, token, model, timeoutHandle)
    try {
      await s.connect()
    } catch (e) {
      // connect() already invokes fail() which clears the timeout; just rethrow.
      throw e
    }
    return s
  }

  private async connect(): Promise<void> {
    const client = new GoogleGenAI({ apiKey: this.token, apiVersion: 'v1alpha' })

    // Tool declarations the scan prompt expects. Keep argument names aligned
    // with the system prompts in scans.ts.
    const tools = [{
      functionDeclarations: [
        {
          name: 'report_progress',
          description: 'Mark a progress checkpoint as confirmed so the UI advances.',
          parametersJsonSchema: {
            type: 'object',
            properties: { step: { type: 'string' } },
            required: ['step'],
          },
        },
        {
          name: 'update_findings',
          description: 'Stream a partial findings patch so the customer sees your current estimates live. Call multiple times to revise. Final values are still submitted via advance_step.',
          parametersJsonSchema: {
            type: 'object',
            properties: { patch: { type: 'object' } },
            required: ['patch'],
          },
        },
        {
          name: 'save_picture',
          description: "Capture the current camera frame as a still for the customer's record. Call this when the customer is pointing at exactly the right thing (label legible, edge clearly framed, etc.). The label is shown to the customer.",
          parametersJsonSchema: {
            type: 'object',
            properties: {
              reason: { type: 'string', description: 'Why this moment matters (internal, not shown to user)' },
              label: { type: 'string', description: 'Short caption shown to the customer, e.g. "Overhang from cabinet face"' },
            },
            required: ['reason', 'label'],
          },
        },
        {
          name: 'verify_capture',
          description: "Run a one-shot verification pass: a still of the current frame is sent to Gemini 3.1 Flash with a scan-specific OCR/inspection prompt. Returns {quality: 'good'|'blurry'|'wrong-frame', notes, findings}. Call this BEFORE save_picture to confirm the frame is usable; if quality is 'blurry' or 'wrong-frame', read the notes aloud to the customer and ask them to re-aim. Only call save_picture + update_findings + report_progress once verify_capture returns quality='good'.",
          parametersJsonSchema: {
            type: 'object',
            properties: {
              focus: { type: 'string', description: 'Optional scan-specific hint. For undersink use one of: valve, drain, outlet, drain-loop. Ignored by model-label and countertop.' },
            },
          },
        },
        {
          name: 'advance_step',
          description: 'Submit the final findings for this scan and end the session.',
          parametersJsonSchema: {
            type: 'object',
            properties: { findings: { type: 'object' } },
            required: ['findings'],
          },
        },
      ],
    }]

    try {
      this.session = await client.live.connect({
        model: this.model,
        callbacks: {
          onopen: () => { /* wait for setupComplete before sending anything */ },
          onmessage: (msg: LiveServerMessage) => { this.handleMessage(msg) },
          onerror: (e: ErrorEvent) => {
            const detail = e.message ?? String(e)
            console.warn('[LiveSession] WebSocket error:', detail, e)
            this.fail('session-drop', detail)
          },
          onclose: (e: CloseEvent) => {
            // The Live API reports the actual server-side rejection reason in
            // CloseEvent.reason (e.g. "config mismatch", "token expired",
            // "auth failed"). Swallowing it made this very hard to debug; keep
            // it loud.
            const detail = `code=${e.code} reason=${e.reason || '(empty)'} wasClean=${e.wasClean}`
            if (!this.aborted) {
              console.warn('[LiveSession] WebSocket closed:', detail)
              this.fail('session-drop', detail)
            } else {
              console.info('[LiveSession] WebSocket closed (post-abort):', detail)
            }
          },
        },
        config: {
          responseModalities: [Modality.AUDIO],
          systemInstruction: { parts: [{ text: this.args.config.systemPrompt }] },
          tools,
        },
      })
    } catch (e) {
      this.fail('session-drop', String(e))
      throw e
    }
  }

  private handleMessage(msg: LiveServerMessage): void {
    if (msg.setupComplete) {
      this.setupComplete = true
      this.startFramePump()
      this.startMicPump()
      this.kickoff()
      return
    }

    if (msg.toolCall?.functionCalls?.length) {
      for (const fc of msg.toolCall.functionCalls) this.handleFunctionCall(fc)
      return
    }

    const serverContent = msg.serverContent
    if (!serverContent) return

    const parts = serverContent.modelTurn?.parts ?? []
    for (const part of parts) {
      const inline = part.inlineData
      if (inline?.mimeType?.startsWith('audio/') && inline.data) {
        this.inboundAudioChunks.push(base64ToBytes(inline.data))
        continue
      }
      if (typeof part.text === 'string' && part.text.length > 0) {
        this.args.callbacks.onCaption(part.text)
      }
    }

    if (serverContent.turnComplete || serverContent.interrupted) {
      this.flushInboundAudio()
      this.signalAudioStreamEnd()
    }
  }

  private handleFunctionCall(fc: FunctionCall): void {
    const name = fc.name ?? ''
    const args = (fc.args ?? {}) as Record<string, unknown>

    if (name === 'report_progress') {
      const step = typeof args.step === 'string' ? args.step : ''
      if (step) this.args.callbacks.onProgress(step)
      this.ackToolCall(fc, { ok: true })
      return
    }

    if (name === 'update_findings') {
      const patch = args.patch as unknown
      const parsed = this.args.config.findingsPatchSchema.safeParse(patch)
      if (!parsed.success) {
        this.ackToolCall(fc, { ok: false, error: `invalid patch: ${parsed.error.message}` })
        return
      }
      this.args.callbacks.onLivePatch?.(parsed.data as Record<string, unknown>)
      this.ackToolCall(fc, { ok: true })
      return
    }

    if (name === 'save_picture') {
      const reason = typeof args.reason === 'string' ? args.reason : ''
      const label = typeof args.label === 'string' ? args.label.trim() : ''
      if (!label) {
        this.ackToolCall(fc, { ok: false, error: 'label is required' })
        return
      }
      // Sample at full canvas resolution, then downscale to keep
      // sessionStorage usage bounded (~3 pictures × ~150 KB ≤ well under cap).
      const fullData = sampleFrame(
        this.args.hiddenCanvas,
        this.args.hiddenCanvas.width,
        this.args.hiddenCanvas.height,
        0.9,
      )
      if (fullData.length === 0) {
        this.ackToolCall(fc, { ok: false, error: 'frame not ready' })
        return
      }
      // `reason` is the model's rationale — kept in the console for debugging,
      // never displayed and never persisted. No telemetry pipeline by design
      // (this is a customer-facing demo, not a metrics surface).
      console.debug('[LiveSession] save_picture', { label, reason })
      // Ack the model immediately; downscaling happens async and is purely
      // a UI-side concern. The model doesn't need the image back — it
      // already knows what it just framed.
      this.ackToolCall(fc, { ok: true })
      void downscaleDataUrl(fullData, SAVED_PICTURE_MAX_EDGE).then(dataUrl => {
        this.args.callbacks.onPictureSaved?.({
          scan: this.args.config.id,
          label,
          dataUrl,
          takenAt: Date.now(),
        })
      }).catch(() => { /* a broken still shouldn't kill the scan */ })
      return
    }

    if (name === 'verify_capture') {
      const focus = typeof args.focus === 'string' ? args.focus : undefined
      // Sample at full canvas resolution so the verifier has the maximum
      // pixel budget for OCR / spatial reasoning.
      const imageBase64 = sampleFrame(
        this.args.hiddenCanvas,
        this.args.hiddenCanvas.width,
        this.args.hiddenCanvas.height,
        0.9,
      )
      if (imageBase64.length === 0) {
        this.ackToolCall(fc, { ok: false, error: 'frame not ready' })
        return
      }
      this.args.callbacks.onVerifyStart?.()
      void verifyCapture(this.args.config.id, imageBase64, focus)
        .then(result => {
          this.ackToolCall(fc, { ok: true, ...result })
        })
        .catch(e => {
          console.warn('[LiveSession] verify_capture failed:', e)
          this.ackToolCall(fc, { ok: false, error: String(e) })
        })
        .finally(() => {
          this.args.callbacks.onVerifyEnd?.()
        })
      return
    }

    if (name === 'advance_step') {
      const findings = (args.findings ?? args) as unknown
      this.ackToolCall(fc, { ok: true })
      this.args.callbacks.onFindings(findings)
      this.close()
      return
    }

    // Unknown tool — ack with an error so the model can recover.
    this.ackToolCall(fc, { ok: false, error: `unknown tool: ${name}` })
  }

  private ackToolCall(fc: FunctionCall, response: Record<string, unknown>): void {
    if (!this.session) return
    const fr: FunctionResponse = {
      ...(fc.id !== undefined && { id: fc.id }),
      ...(fc.name !== undefined && { name: fc.name }),
      response,
    }
    try { this.session.sendToolResponse({ functionResponses: [fr] }) } catch { /* fall through */ }
  }

  private flushInboundAudio(): void {
    if (this.inboundAudioChunks.length === 0) return
    const total = this.inboundAudioChunks.reduce((n, c) => n + c.byteLength, 0)
    const merged = new Uint8Array(total)
    let offset = 0
    for (const c of this.inboundAudioChunks) { merged.set(c, offset); offset += c.byteLength }
    this.inboundAudioChunks = []
    try {
      const blob = pcmToWavBlob(merged, GEMINI_OUTPUT_SAMPLE_RATE, 1)
      this.args.callbacks.onTurnAudio?.(blob)
    } catch { /* a broken audio chunk shouldn't kill the scan */ }
  }

  /**
   * Nudge the model to start the conversation immediately rather than
   * waiting for the user's first audio. Without this the user stares at a
   * silent camera for several seconds while VAD waits for speech that's
   * never coming. The kickoff is a single user-role text turn — the model
   * responds with its first coaching line, which fires `onCaption` and the
   * audio turn, getting the scan moving.
   */
  private kickoff(): void {
    if (!this.session) return
    try {
      this.session.sendClientContent({
        turns: "I'm ready. Please give me your first instruction.",
        turnComplete: true,
      })
    } catch (e) {
      console.warn('[LiveSession] kickoff failed:', e)
    }
  }

  /**
   * Tell Gemini the user's audio "turn" is complete so it doesn't keep
   * listening to the open mic while its own response plays back through the
   * device speaker (which the mic picks up). The mic loop continues to run
   * and re-engages naturally once the user crosses the silence threshold.
   */
  private signalAudioStreamEnd(): void {
    if (!this.session || !this.setupComplete) return
    try {
      this.session.sendRealtimeInput({ audioStreamEnd: true })
      this.pendingAudioEnd = true
    } catch { /* fall through; onerror will tear us down if fatal */ }
  }

  private startFramePump(): void {
    if (this.frameTimer !== null) return
    this.frameTimer = window.setInterval(() => {
      if (!this.session || !this.setupComplete) return
      try {
        const data = sampleFrame(this.args.hiddenCanvas, FRAME_WIDTH, FRAME_HEIGHT, FRAME_QUALITY)
        if (data.length > 0) {
          this.session.sendRealtimeInput({ video: { mimeType: 'image/jpeg', data } })
        }
      } catch { /* drop this frame; onerror handles a dead session */ }
    }, FRAME_INTERVAL_MS)
  }

  private startMicPump(): void {
    if (this.audioSource || this.audioProcessor) return
    try {
      const ctx = this.args.audioContext
      const source = ctx.createMediaStreamSource(this.args.audioStream)
      // ScriptProcessor is deprecated but universally supported; AudioWorklet
      // would require shipping a separate worklet module. Good enough for this
      // single-scan use case.
      const processor = ctx.createScriptProcessor(4096, 1, 1)
      processor.onaudioprocess = (ev: AudioProcessingEvent): void => {
        if (!this.session || !this.setupComplete || this.aborted) return
        const ch = ev.inputBuffer.getChannelData(0)

        // While the model is talking back, suppress sub-threshold (silence)
        // frames so we don't re-feed the model's own audio. The moment the
        // user crosses the threshold we resume normal streaming.
        if (this.pendingAudioEnd) {
          let peak = 0
          for (let i = 0; i < ch.length; i++) {
            const v = Math.abs(ch[i] ?? 0)
            if (v > peak) peak = v
          }
          if (peak < SILENCE_PEAK_THRESHOLD) return
          this.pendingAudioEnd = false
        }

        const pcm = downsampleFloat32ToPcm16(ch, ctx.sampleRate, GEMINI_INPUT_SAMPLE_RATE)
        if (pcm.byteLength === 0) return
        try {
          this.session.sendRealtimeInput({
            audio: { mimeType: `audio/pcm;rate=${GEMINI_INPUT_SAMPLE_RATE}`, data: bytesToBase64(pcm) },
          })
        } catch { /* dropped; onerror handles fatal cases */ }
      }
      source.connect(processor)
      // Connect to destination so the processor actually runs in some browsers.
      processor.connect(ctx.destination)
      this.audioSource = source
      this.audioProcessor = processor
    } catch { /* no mic capture — frames still go through */ }
  }

  private fail(reason: AbortReason, detail?: string): void {
    if (this.aborted) return
    this.aborted = true
    this.args.callbacks.onAbort(reason, detail)
    this.close()
  }

  /** External: user tapped "Skip — I'll enter manually". */
  cancel(): void { this.fail('user-cancelled') }

  close(): void {
    if (this.frameTimer !== null) { clearInterval(this.frameTimer); this.frameTimer = null }
    if (this.timeoutHandle !== null) { clearTimeout(this.timeoutHandle); this.timeoutHandle = null }
    if (this.audioProcessor) { try { this.audioProcessor.disconnect() } catch { /* */ }; this.audioProcessor = null }
    if (this.audioSource) { try { this.audioSource.disconnect() } catch { /* */ }; this.audioSource = null }
    // Do NOT close args.audioContext — the caller owns its lifetime. Closing
    // it on iOS burns the user-gesture autoplay grant.
    if (this.session) { try { this.session.close() } catch { /* */ }; this.session = null }
    // Do NOT stop the source MediaStream tracks here. The parent screen
    // owns the grant (via useGrantLifetime) and stops the tracks at
    // unmount; touching them here means a transient WebSocket failure or
    // any other AbortReason kills the visible <video> too, leaving the user
    // staring at a black screen with no obvious cause.
    this.inboundAudioChunks = []
  }
}
